import React from 'react'
import Text from '../../components/text/Text'
import {IVolumeLeague} from '../../apis/volume_league/list'
import {IGroup} from './types'
import {kindTagClass} from './utils'
import img_placeholder from '../../assets/images/placeholder.png'

interface IProps {
    groups: IGroup[]
    dragEnabled: boolean
    onReorder: (from: string, to: string) => void
    onEnterGroup: (group: string) => void
    cardLogo: (l?: IVolumeLeague) => string
}

/**
 * 허브(전체 그룹) 목록 — 그룹 대표 행만 표시.
 *  - 누르면 그 그룹 필터가 켜지며 항목 목록으로 진입.
 *  - 전체 상태 + 전체 그룹에서는 핸들(≡)을 드래그해 그룹 단위 sort_value 변경 가능.
 *  - 드래그 시각 상태(끌고 있는 그룹/드롭 위치)는 이 컴포넌트가 직접 소유하고,
 *    실제 순서 변경은 onReorder(from, to) 로 위임한다.
 */
const GroupHubList: React.FC<IProps> = ({groups, dragEnabled, onReorder, onEnterGroup, cardLogo}) => {
    const dragGroup = React.useRef<string | null>(null)
    // 드래그 중 커서가 올라간 그룹 (드롭 위치 파란 박스 표시용)
    const [dragOverGroup, setDragOverGroup] = React.useState<string | null>(null)

    const drop = (target: string) => {
        const from = dragGroup.current
        dragGroup.current = null
        setDragOverGroup(null)
        if (from) onReorder(from, target)
    }

    if (groups.length === 0) {
        return <Text className="text-body2 text-font03">{'그룹이 없습니다.'}</Text>
    }
    return (
        <>
            {groups.map(g => {
                const repLeague = g.cards[0]?.rep
                const hasRolling = g.cards.some(c => c.rep?.kind === 'rolling')
                const hasComp = g.cards.some(c => c.rep?.kind === 'competition')
                const itemCount = g.cards.reduce((m, c) => m + c.items.length, 0)
                return (
                    <React.Fragment key={g.group}>
                        {/* 드롭될 자리 표시 — 리스트 사이에 파란 박스 삽입 */}
                        {dragEnabled && dragOverGroup === g.group && dragGroup.current !== g.group && (
                            <div
                                onDragEnter={() => setDragOverGroup(g.group)}
                                onDragOver={e => e.preventDefault()}
                                onDrop={() => drop(g.group)}
                                className="h-[44px] rounded-[6px] border-2 border-dashed border-sub_blue bg-blue-50/60"
                            />
                        )}
                        <div
                            draggable={dragEnabled}
                            onDragStart={() => (dragGroup.current = g.group)}
                            onDragEnter={() =>
                                dragEnabled &&
                                dragGroup.current &&
                                dragGroup.current !== g.group &&
                                setDragOverGroup(g.group)
                            }
                            onDragOver={e => e.preventDefault()}
                            onDragEnd={() => {
                                dragGroup.current = null
                                setDragOverGroup(null)
                            }}
                            onDrop={() => drop(g.group)}
                            onClick={() => onEnterGroup(g.group)}
                            className={`p-4 border border-line02 bg-white rounded-[6px] flex items-center gap-x-3 transition-shadow cursor-pointer hover:ring-1 hover:ring-inset hover:ring-primary ${
                                dragEnabled ? 'cursor-grab' : ''
                            }`}>
                            {/* 드래그 핸들 (정렬 가능 시) / 진입 화살표 (그 외). 핸들 클릭은 진입과 분리 */}
                            <div onClick={e => dragEnabled && e.stopPropagation()} className="select-none">
                                <Text className="text-h3 text-font03">{dragEnabled ? '≡' : '›'}</Text>
                            </div>
                            <img
                                src={cardLogo(repLeague) || img_placeholder}
                                onError={e => ((e.target as HTMLImageElement).src = img_placeholder)}
                                className="w-10 h-10 min-w-[40px] rounded-full object-cover bg-line03"
                                alt=""
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-x-2 flex-wrap">
                                    <Text className="text-ti1 font-semibold text-font01 truncate max-w-[220px]">
                                        {g.group}
                                    </Text>
                                    <Text className="text-ti3 text-font03">{itemCount}개 항목</Text>
                                </div>
                                <div className="mt-[8px] flex items-center gap-x-2 flex-wrap gap-y-[6px]">
                                    {hasRolling && (
                                        <Text className={`text-ti3 px-2 py-[2px] rounded-[4px] ${kindTagClass('rolling')}`}>
                                            상시 순위표
                                        </Text>
                                    )}
                                    {hasComp && (
                                        <Text
                                            className={`text-ti3 px-2 py-[2px] rounded-[4px] ${kindTagClass(
                                                'competition',
                                            )}`}>
                                            기간 대회
                                        </Text>
                                    )}
                                </div>
                            </div>
                            <Text className="text-ti3 font-bold text-sub_blue whitespace-nowrap">
                                sort_value: {g.sortValue}
                            </Text>
                        </div>
                    </React.Fragment>
                )
            })}
        </>
    )
}

export default GroupHubList
